import { create } from 'zustand';

// Page hierarchy for determining forward/back navigation
const PAGE_DEPTH: Record<string, number> = {
  welcome: 0,
  station: 1,
  housekeeping: 1,
  maintenance: 1,
  guides: 1,
  messages: 1,
  notifications: 1,
  vouchers: 1,
  profile: 1,
  'register-rooms': 1,
  'my-rooms': 1,
  order: 2,
};

function getPageDepth(pathname: string): number {
  for (const [key, depth] of Object.entries(PAGE_DEPTH)) {
    if (pathname.includes(`/${key}`)) return depth;
  }
  return 0;
}

export type NavigationDirection = 'forward' | 'back' | 'none';

interface NavigationState {
  direction: NavigationDirection;
  previousPath: string | null;
  currentPath: string;
  /** Map of page path → page title for the inner-page header */
  pageTitles: Record<string, string>;
  setCurrentPath: (path: string) => void;
  setPageTitle: (path: string, title: string) => void;
  getPageTitle: (path: string) => string | undefined;
}

export const useNavigationStore = create<NavigationState>((set, get) => ({
  direction: 'none',
  previousPath: null,
  currentPath: '/',
  pageTitles: {},

  setCurrentPath: (path: string) => {
    const state = get();
    if (path === state.currentPath) return;

    const prevDepth = getPageDepth(state.currentPath);
    const nextDepth = getPageDepth(path);

    let direction: NavigationDirection;
    if (nextDepth > prevDepth) {
      direction = 'forward';
    } else if (nextDepth < prevDepth) {
      direction = 'back';
    } else {
      // Same level navigation — treat as forward
      direction = state.currentPath === '/' ? 'none' : 'forward';
    }

    set({
      direction,
      previousPath: state.currentPath,
      currentPath: path,
    });
  },

  setPageTitle: (path: string, title: string) => {
    set((state) => ({
      pageTitles: { ...state.pageTitles, [path]: title },
    }));
  },

  getPageTitle: (path: string) => {
    return get().pageTitles[path];
  },
}));
